<template>
    <v-content>
        <v-container fill-height fluid>
            <v-layout align-center justify-center>
                <v-flex lg5 md5 sm8 xs12 id="form-two-factor">
                    <div class="pa-3">
                        <!-- Logo -->
                        <div class="layout column align-center">
                            <img alt="cibes login" src="/images/logo.png" style="height: 70px;">
                        </div>

                        <div class="pt-4">
                            <!-- Shield icon + title -->
                            <div class="tf-header">
                                <div class="tf-shield">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 2L4 6v6c0 5.25 3.4 10.15 8 11.4C16.6 22.15 20 17.25 20 12V6L12 2z" fill="#0779FF" opacity="0.15"/>
                                        <path d="M12 2L4 6v6c0 5.25 3.4 10.15 8 11.4C16.6 22.15 20 17.25 20 12V6L12 2z" stroke="#0779FF" stroke-width="1.5" fill="none"/>
                                        <path d="M9 12l2 2 4-4" stroke="#0779FF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </div>
                                <h3 class="tf-title">Two-Factor Authentication</h3>
                                <p class="tf-subtitle">Choose how to receive your verification code</p>
                            </div>

                            <!-- Error -->
                            <p class="msg-error" v-if="error_message">{{ error_message }}</p>

                            <!-- Channel -->
                            <span class="field-label">Receive code via</span>
                            <div class="channel-tabs">
                                <div
                                    class="channel-tab"
                                    :class="{ 'channel-tab--active': channel === 'EMAIL' }"
                                    @click="selectChannel('EMAIL')"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" stroke-width="1.5"/>
                                        <path d="M2 8l10 6 10-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                                    </svg>
                                    Email
                                </div>
                                <div
                                    class="channel-tab"
                                    :class="{ 'channel-tab--active': channel === 'SMS' }"
                                    @click="selectChannel('SMS')"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" stroke-width="1.5"/>
                                        <circle cx="12" cy="18" r="1" fill="currentColor"/>
                                    </svg>
                                    SMS
                                </div>
                            </div>

                            <!-- Send Code -->
                            <v-btn
                                block
                                :color="countdown > 0 ? '' : 'primary'"
                                :loading="sending"
                                :disabled="countdown > 0"
                                @click="sendCode"
                                class="btn-action"
                                :class="{ 'btn-countdown': countdown > 0 }"
                            >
                                <span v-if="countdown > 0">Resend in {{ countdown }}s</span>
                                <span v-else-if="codeSent">Resend Code</span>
                                <span v-else>Send Code</span>
                            </v-btn>

                            <!-- OTP section -->
                            <template v-if="codeSent">
                                <div class="section-divider"></div>

                                <div class="otp-label-row">
                                    <span class="field-label">Verification Code</span>
                                    <span class="expiry-badge">Expires in 15 min</span>
                                </div>

                                <v-text-field
                                    v-model="code"
                                    solo
                                    placeholder="------"
                                    maxlength="6"
                                    :disabled="verifying"
                                    class="otp-field"
                                    v-on:keyup.enter="verifyCode"
                                    @input="onCodeInput"
                                ></v-text-field>

                                <p class="attempts-warning" v-if="failedAttempts > 0">
                                    ⚠ {{ 3 - failedAttempts }} attempt(s) remaining
                                </p>

                                <v-btn
                                    block
                                    color="primary"
                                    :loading="verifying"
                                    :disabled="code.length !== 6 || verifying"
                                    @click="verifyCode"
                                    class="btn-action"
                                >
                                    Verify &amp; Continue
                                </v-btn>
                            </template>

                            <!-- Back -->
                            <div class="back-row">
                                <span class="back-link" @click="backToLogin">← Back to Login</span>
                            </div>
                        </div>
                    </div>
                </v-flex>
            </v-layout>
        </v-container>
    </v-content>
</template>

<script>
const MAX_ATTEMPTS = 3;
const RESEND_BASE_COOLDOWN = 30;   // giây, lần đầu
const RESEND_MAX_COOLDOWN = 300;   // cap tối đa 5 phút

export default {
    metaInfo() {
        return { title: "Two-Factor Authentication" };
    },
    data: () => ({
        channel: 'EMAIL',
        code: '',
        codeSent: false,
        sending: false,
        verifying: false,
        countdown: 0,
        resendCount: 0,
        failedAttempts: 0,
        error_message: '',
        countdownTimer: null
    }),
    computed: {
        preAuthToken() {
            return this.$store.getters['auth/preAuthToken'];
        }
    },
    mounted() {
        if (!this.preAuthToken) {
            this.$router.replace('/login');
        }
    },
    beforeDestroy() {
        this.clearCountdown();
    },
    methods: {
        selectChannel(ch) {
            if (this.countdown > 0) return;
            this.channel = ch;
        },
        sendCode() {
            this.sending = true;
            this.error_message = '';
            this.$axios
                .post('/auth-v2/generate-code', { channel: this.channel }, {
                    headers: { Authorization: `Bearer ${this.preAuthToken}` }
                })
                .then(res => {
                    this.sending = false;
                    if (res.data.statusCode !== 200) {
                        this.error_message = res.data.message;
                        return;
                    }
                    this.codeSent = true;
                    this.code = '';
                    this.resendCount++;
                    this.startCountdown();
                })
                .catch(() => {
                    this.sending = false;
                    this.error_message = 'Failed to send code. Please try again.';
                });
        },
        startCountdown() {
            this.clearCountdown();
            const backoff = RESEND_BASE_COOLDOWN * Math.pow(2, this.resendCount - 1);
            this.countdown = Math.min(backoff, RESEND_MAX_COOLDOWN);
            this.countdownTimer = setInterval(() => {
                this.countdown--;
                if (this.countdown <= 0) this.clearCountdown();
            }, 1000);
        },
        clearCountdown() {
            if (this.countdownTimer) {
                clearInterval(this.countdownTimer);
                this.countdownTimer = null;
            }
            this.countdown = 0;
        },
        onCodeInput() {
            this.error_message = '';
            if (this.code.length === 6) this.verifyCode();
        },
        verifyCode() {
            if (this.code.length !== 6 || this.verifying) return;
            this.verifying = true;
            this.error_message = '';
            this.$axios
                .post('/auth-v2/verify-code', { code: this.code }, {
                    headers: { Authorization: `Bearer ${this.preAuthToken}` }
                })
                .then(res => {
                    this.verifying = false;
                    if (res.data.statusCode !== 200) {
                        this.code = '';
                        this.failedAttempts++;
                        if (this.failedAttempts >= MAX_ATTEMPTS) { this.handleBlocked(); return; }
                        this.error_message = res.data.message || 'Invalid code. Please try again.';
                        return;
                    }
                    this.$store.dispatch('auth/clearPreAuthToken');
                    this.$store.dispatch('auth/login', res.data.data.token);
                    this.$router.push('/dashboard');
                })
                .catch(() => {
                    this.verifying = false;
                    this.code = '';
                    this.failedAttempts++;
                    if (this.failedAttempts >= MAX_ATTEMPTS) { this.handleBlocked(); return; }
                    this.error_message = 'Invalid code. Please try again.';
                });
        },
        handleBlocked() {
            this.clearCountdown();
            this.$store.dispatch('auth/clearPreAuthToken');
            this.$router.replace({ path: '/login', query: { blocked: 'true' } });
        },
        backToLogin() {
            this.clearCountdown();
            this.$store.dispatch('auth/clearPreAuthToken');
            this.$router.replace('/login');
        }
    }
};
</script>

<style scoped>
#form-two-factor {
    max-width: 452px;
}

/* Header */
.tf-header {
    text-align: center;
    margin-bottom: 20px;
}
.tf-shield {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: rgba(7, 121, 255, 0.12);
    border: 1px solid rgba(7, 121, 255, 0.25);
    margin-bottom: 10px;
}
.tf-title {
    color: #c8d0d8;
    font-size: 17px;
    font-weight: 600;
    margin: 0 0 5px;
}
.tf-subtitle {
    color: #8F969D;
    font-size: 13px;
    margin: 0;
}

/* Error */
.msg-error {
    color: #FF5E44;
    font-size: 12px;
    letter-spacing: 0.5px;
    margin-bottom: 10px;
}

/* Labels */
.field-label {
    color: #8F969D;
    text-transform: uppercase;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.8px;
    display: block;
    margin-bottom: 8px;
}

/* Channel tabs */
.channel-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
}
.channel-tab {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 11px 8px;
    background: #26313E;
    border: 1.5px solid transparent;
    border-radius: 6px;
    color: #8F969D;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
    user-select: none;
}
.channel-tab--active {
    border-color: #0779FF;
    color: #0779FF;
}
.channel-tab:not(.channel-tab--active):hover {
    border-color: #445566;
    color: #a0a8b0;
}

/* Buttons */
.btn-action {
    text-transform: inherit !important;
    font-size: 14px !important;
    height: 42px !important;
    letter-spacing: 0.2px !important;
}
.btn-countdown {
    background-color: #26313E !important;
    color: #8F969D !important;
    box-shadow: none !important;
}
.btn-countdown >>> .v-btn__content {
    color: #8F969D !important;
}

/* Divider */
.section-divider {
    height: 1px;
    background: rgba(255,255,255,0.07);
    margin: 18px 0;
}

/* OTP */
.otp-label-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}
.expiry-badge {
    font-size: 11px;
    color: #8F969D;
}
.otp-field >>> .v-input__control .v-input__slot {
    background-color: #26313E !important;
}
.otp-field >>> input {
    color: #c8d0d8;
    font-size: 22px;
    letter-spacing: 10px;
    text-align: center;
    font-weight: 600;
    padding: 4px 0;
}
.otp-field >>> input::placeholder {
    color: #445566;
    font-size: 16px;
    letter-spacing: 8px;
    font-weight: 400;
}

/* Attempts */
.attempts-warning {
    color: #ffa726;
    font-size: 12px;
    margin-bottom: 8px;
}

/* Back */
.back-row {
    text-align: center;
    margin-top: 20px;
}
.back-link {
    color: #3498db;
    font-size: 13px;
    cursor: pointer;
}
.back-link:hover {
    text-decoration: underline;
}
</style>
